import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const GameContext = createContext();

/**
 * GameProvider - Context provider for game state management
 * Handles saving/loading game progress, high scores, and levels
 */
export const GameProvider = ({ children }) => {
  // State for all games
  const [gameState, setGameState] = useState({
    asteroids: {
      highScore: 0,
      level: 1,
      progress: 0,
    },
    spaceInvaders: {
      highScore: 0,
      level: 1,
      progress: 0,
    },
    pong: {
      highScore: 0,
      level: 1,
      progress: 0,
    },
  });

  // Load game state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('wondernet-games');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        setGameState(prevState => ({
          ...prevState,
          ...parsedState,
        }));
      } catch (error) {
        console.error('Error loading game state:', error);
      }
    }
  }, []);

  // Save game state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('wondernet-games', JSON.stringify(gameState));
  }, [gameState]);

  // Update game state for a specific game
  const updateGameState = (gameId, newState) => {
    setGameState(prevState => ({
      ...prevState,
      [gameId]: {
        ...prevState[gameId],
        ...newState,
        // Update high score if the new score is higher
        highScore: newState.score > prevState[gameId].highScore 
          ? newState.score 
          : prevState[gameId].highScore,
      },
    }));
  };

  // Reset progress for a specific game
  const resetGame = (gameId) => {
    setGameState(prevState => ({
      ...prevState,
      [gameId]: {
        ...prevState[gameId],
        level: 1,
        progress: 0,
      },
    }));
  };

  // Reset all game progress
  const resetAllGames = () => {
    const resetState = Object.keys(gameState).reduce((acc, gameId) => {
      acc[gameId] = {
        ...gameState[gameId],
        level: 1,
        progress: 0,
      };
      return acc;
    }, {});
    
    setGameState(resetState);
  };

  // Context value
  const value = {
    gameState,
    updateGameState,
    resetGame,
    resetAllGames,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

// Custom hook for using the game context
export const useGameContext = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};

export default GameContext;